import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import type { AdminStats, DatabaseInfo, GameCreationStat, GameSuggestion } from "@/types";

const ADMIN_SETTINGS_QUERY = gql`
  query AdminSettings($keys: [String!]) {
    adminSettings(keys: $keys) {
      key
      value
    }
  }
`;

const UPDATE_SETTING_MUTATION = gql`
  mutation UpdateSetting($key: String!, $value: String!) {
    updateSetting(key: $key, value: $value) {
      setting { key value }
      errors
    }
  }
`;

const ADMIN_STATS_QUERY = gql`
  query {
    adminStats {
      totalUsers
      activeUsers
      totalListings
      activeListings
      totalGames
      totalAiRequests
      aiErrorCount
      aiRequestsToday
      aiErrorsToday
      totalErrorLogs
      errorLogsToday
      gamesToday
      gamesThisWeek
      pendingGameSuggestionsCount
      jobsFailed
      jobsScheduled
      jobsInProgress
    }
  }
`;

const GAME_CREATION_STATS_QUERY = gql`
  query GameCreationStats($days: Int) {
    adminGameCreationStats(days: $days) {
      date
      count
    }
  }
`;

const GAME_COMPLETION_STATS_QUERY = gql`
  query GameCompletionStats($days: Int) {
    adminGameCompletionStats(days: $days) {
      date
      count
    }
  }
`;

const GAME_SUGGESTIONS_QUERY = gql`
  query GameSuggestions($status: String) {
    gameSuggestions(status: $status) {
      id
      url
      title
      status
      user { id displayName }
      listing { id }
      createdAt
    }
  }
`;

const RESOLVE_GAME_SUGGESTION_MUTATION = gql`
  mutation ResolveGameSuggestion($id: ID!) {
    resolveGameSuggestion(id: $id) {
      gameSuggestion { id status }
      errors
    }
  }
`;

const IGNORE_GAME_SUGGESTION_MUTATION = gql`
  mutation IgnoreGameSuggestion($id: ID!) {
    ignoreGameSuggestion(id: $id) {
      gameSuggestion { id status }
      errors
    }
  }
`;

function StatCard({
  label,
  details,
  color = "gray",
  to,
  icon,
}: {
  label: string;
  details: { value: number; sublabel: string }[];
  color?: string;
  to?: string;
  icon: React.ReactNode;
}) {
  const colorClasses: Record<string, { card: string; icon: string }> = {
    gray: { card: "bg-gray-50 border-gray-200", icon: "bg-gray-100 text-gray-600" },
    blue: { card: "bg-blue-50 border-blue-200", icon: "bg-blue-100 text-blue-600" },
    green: { card: "bg-green-50 border-green-200", icon: "bg-green-100 text-green-600" },
    red: { card: "bg-red-50 border-red-200", icon: "bg-red-100 text-red-600" },
    yellow: { card: "bg-yellow-50 border-yellow-200", icon: "bg-yellow-100 text-yellow-600" },
    purple: { card: "bg-purple-50 border-purple-200", icon: "bg-purple-100 text-purple-600" },
    indigo: { card: "bg-indigo-50 border-indigo-200", icon: "bg-indigo-100 text-indigo-600" },
  };

  const colors = colorClasses[color] || colorClasses.gray;
  const [primary, ...secondary] = details;

  const content = (
    <>
      <div className="flex items-center gap-2">
        <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${colors.icon}`}>
          {icon}
        </div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
      </div>
      {primary && (
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-2xl font-bold text-gray-900">{primary.value}</span>
          <span className="text-xs text-gray-500">{primary.sublabel}</span>
        </div>
      )}
      {secondary.length > 0 && (
        <div className="mt-1 flex gap-3 text-xs text-gray-500">
          {secondary.map((d, i) => (
            <span key={i}>
              <span className="font-semibold text-gray-700">{d.value}</span>{" "}
              {d.sublabel}
            </span>
          ))}
        </div>
      )}
    </>
  );

  const className = `rounded-xl border p-4 ${colors.card}${to ? " transition-shadow hover:shadow-md" : ""}`;

  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
}

function QuickLinkCard({
  title,
  description,
  to,
  icon,
}: {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-800">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>
    </Link>
  );
}

function GameCreationChart({ stats }: { stats: GameCreationStat[] }) {
  const maxCount = Math.max(...stats.map((s) => s.count), 1);

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="font-semibold text-gray-900">Nauji žaidimai per dieną</h3>
      <div className="mt-4 space-y-1">
        {stats.map((stat) => (
          <div key={stat.date} className="flex items-center gap-3 text-sm">
            <span className="w-24 shrink-0 text-gray-500">
              {new Date(stat.date).toLocaleDateString("lt-LT", {
                month: "short",
                day: "numeric",
              })}
            </span>
            <div className="flex-1">
              <div
                className="h-5 rounded bg-primary-600"
                style={{ width: `${(stat.count / maxCount) * 100}%` }}
              />
            </div>
            <span className="w-12 text-right font-medium text-gray-900">
              {stat.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GameCompletionChart({ stats }: { stats: GameCreationStat[] }) {
  const maxCount = Math.max(...stats.map((s) => s.count), 1);

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="font-semibold text-gray-900">Pilna informacija per dieną</h3>
      <div className="mt-4 space-y-1">
        {stats.map((stat) => (
          <div key={stat.date} className="flex items-center gap-3 text-sm">
            <span className="w-24 shrink-0 text-gray-500">
              {new Date(stat.date).toLocaleDateString("lt-LT", {
                month: "short",
                day: "numeric",
              })}
            </span>
            <div className="flex-1">
              <div
                className="h-5 rounded bg-green-600"
                style={{ width: `${(stat.count / maxCount) * 100}%` }}
              />
            </div>
            <span className="w-12 text-right font-medium text-gray-900">
              {stat.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsSection() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["adminSettings"],
    queryFn: () =>
      graphqlClient.request<{ adminSettings: { key: string; value: string | null }[] }>(
        ADMIN_SETTINGS_QUERY,
        { keys: ["gemini_paid_disabled"] },
      ),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      graphqlClient.request(UPDATE_SETTING_MUTATION, { key, value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettings"] });
    },
  });

  const geminiPaidDisabled = data?.adminSettings.find(
    (s) => s.key === "gemini_paid_disabled",
  )?.value === "true";

  const toggleGeminiPaid = useCallback(() => {
    updateMutation.mutate({
      key: "gemini_paid_disabled",
      value: geminiPaidDisabled ? "false" : "true",
    });
  }, [geminiPaidDisabled, updateMutation]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900">Gemini mokama API</p>
          <p className="mt-0.5 text-sm text-gray-500">
            {geminiPaidDisabled
              ? "Mokama API išjungta — naudojama tik nemokama pakopa"
              : "Mokama API įjungta — naudojama kai nemokama pakopa nepavyksta"}
          </p>
        </div>
        <button
          onClick={toggleGeminiPaid}
          disabled={updateMutation.isPending}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
            geminiPaidDisabled ? "bg-gray-300" : "bg-green-500"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              geminiPaidDisabled ? "translate-x-0" : "translate-x-5"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function GameSuggestionsSection() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["gameSuggestions", "pending"],
    queryFn: () =>
      graphqlClient.request<{ gameSuggestions: GameSuggestion[] }>(
        GAME_SUGGESTIONS_QUERY,
        { status: "pending" },
      ),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) =>
      graphqlClient.request(RESOLVE_GAME_SUGGESTION_MUTATION, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gameSuggestions"] });
      queryClient.invalidateQueries({ queryKey: ["adminStats"] });
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: string) =>
      graphqlClient.request(IGNORE_GAME_SUGGESTION_MUTATION, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gameSuggestions"] });
      queryClient.invalidateQueries({ queryKey: ["adminStats"] });
    },
  });

  const suggestions = data?.gameSuggestions || [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <p className="text-sm text-gray-500">Nėra laukiančių pasiūlymų</p>
    );
  }

  return (
    <div className="space-y-3">
      {suggestions.map((suggestion) => (
        <div
          key={suggestion.id}
          className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4"
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900">
              {suggestion.title || "Be pavadinimo"}
            </p>
            <a
              href={suggestion.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block truncate text-sm text-primary-700 hover:text-primary-800"
            >
              {suggestion.url}
            </a>
            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
              <span>{suggestion.user.displayName}</span>
              {suggestion.listing && (
                <Link
                  to={`/listings/${suggestion.listing.id}`}
                  className="text-primary-700 hover:text-primary-800"
                >
                  Skelbimas #{suggestion.listing.id}
                </Link>
              )}
              <span>
                {new Date(suggestion.createdAt).toLocaleDateString("lt-LT")}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              onClick={() => resolveMutation.mutate(suggestion.id)}
              disabled={resolveMutation.isPending}
              className="rounded-lg bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
            >
              Išspręsta
            </button>
            <button
              onClick={() => ignoreMutation.mutate(suggestion.id)}
              disabled={ignoreMutation.isPending}
              className="rounded-lg bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Ignoruoti
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["adminStats"],
    queryFn: () =>
      graphqlClient.request<{ adminStats: AdminStats }>(ADMIN_STATS_QUERY),
  });

  const { data: creationStatsData } = useQuery({
    queryKey: ["gameCreationStats"],
    queryFn: () =>
      graphqlClient.request<{ adminGameCreationStats: GameCreationStat[] }>(
        GAME_CREATION_STATS_QUERY,
        { days: 30 },
      ),
  });

  const { data: completionStatsData } = useQuery({
    queryKey: ["gameCompletionStats"],
    queryFn: () =>
      graphqlClient.request<{ adminGameCompletionStats: GameCreationStat[] }>(
        GAME_COMPLETION_STATS_QUERY,
        { days: 30 },
      ),
  });

  const [dbInfo, setDbInfo] = useState<DatabaseInfo | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    fetch("/admin/database/info", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then(setDbInfo)
      .catch(() => {});
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch("/admin/database/download", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        res.headers.get("content-disposition")?.match(/filename="?(.+)"?/)?.[1] ||
        "database.sqlite3";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const stats = data?.adminStats;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900">Admin valdymo skydas</h1>
      <p className="mt-1 text-sm text-gray-500">Sistemos apžvalga ir valdymas</p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : stats ? (
        <>
          {/* Stats cards */}
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard
              label="Naudotojai"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.053M18 10.5a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm-13.5 0a2.625 2.625 0 115.25 0 2.625 2.625 0 01-5.25 0z" /></svg>}
              details={[
                { value: stats.activeUsers, sublabel: "aktyvūs" },
                { value: stats.totalUsers, sublabel: "iš viso" },
              ]}
              color="blue"
              to="/admin/users"
            />
            <StatCard
              label="Skelbimai"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6h.008v.008H6V6z" /></svg>}
              details={[
                { value: stats.activeListings, sublabel: "aktyvūs" },
                { value: stats.totalListings, sublabel: "iš viso" },
              ]}
              color="green"
              to="/admin/listings"
            />
            <StatCard
              label="Žaidimai"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.491 48.491 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" /></svg>}
              details={[
                { value: stats.gamesToday, sublabel: "šiandien" },
                { value: stats.totalGames, sublabel: "iš viso" },
              ]}
              color="purple"
              to="/admin/games"
            />
            <StatCard
              label="AI užklausos"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></svg>}
              details={[
                { value: stats.aiRequestsToday, sublabel: "šiandien" },
                { value: stats.totalAiRequests, sublabel: "iš viso" },
                { value: stats.aiErrorsToday, sublabel: "klaidos" },
              ]}
              color="blue"
              to="/admin/ai-requests"
            />
            <StatCard
              label="Klaidos"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>}
              details={[
                { value: stats.errorLogsToday, sublabel: "šiandien" },
                { value: stats.totalErrorLogs, sublabel: "iš viso" },
              ]}
              color="yellow"
              to="/admin/error-logs"
            />
            <StatCard
              label="Foninės užduotys"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M21.015 4.356v4.992" /></svg>}
              details={[
                { value: stats.jobsInProgress, sublabel: "vykdomos" },
                { value: stats.jobsScheduled, sublabel: "suplanuotos" },
                { value: stats.jobsFailed, sublabel: "nepavykusios" },
              ]}
              color="indigo"
              to="/admin/jobs"
            />
            <StatCard
              label="Crawl snapshots"
              icon={<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4.5A1.5 1.5 0 014.5 3h15A1.5 1.5 0 0121 4.5v15A1.5 1.5 0 0119.5 21h-15A1.5 1.5 0 013 19.5v-15zM7 7h10M7 11h10M7 15h6" /></svg>}
              details={[]}
              color="green"
              to="/admin/page-snapshots"
            />
          </div>

          {/* Settings */}
          <h2 className="mt-10 text-lg font-semibold text-gray-900">
            Nustatymai
          </h2>
          <div className="mt-4">
            <SettingsSection />
          </div>

          {/* Game suggestions */}
          {stats.pendingGameSuggestionsCount > 0 && (
            <>
              <h2 className="mt-10 flex items-center gap-2 text-lg font-semibold text-gray-900">
                Siūlomi žaidimai
                <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-sm font-medium text-yellow-800">
                  {stats.pendingGameSuggestionsCount}
                </span>
              </h2>
              <div className="mt-4">
                <GameSuggestionsSection />
              </div>
            </>
          )}

          {/* Game creation & completion stats */}
          <div className="mt-10 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Žaidimų statistika
            </h2>
            <Link
              to="/admin/games"
              className="text-sm text-primary-700 hover:text-primary-800"
            >
              Peržiūrėti visus →
            </Link>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {creationStatsData?.adminGameCreationStats && (
              <GameCreationChart
                stats={creationStatsData.adminGameCreationStats}
              />
            )}
            {completionStatsData?.adminGameCompletionStats && (
              <GameCompletionChart
                stats={completionStatsData.adminGameCompletionStats}
              />
            )}
          </div>

          {/* Quick links */}
          <h2 className="mt-10 text-lg font-semibold text-gray-900">
            Valdymo skyriai
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <QuickLinkCard
              title="Analitika"
              description="Lankytojai, botai, populiariausi puslapiai"
              to="/admin/analytics"
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18M7 14l3-3 4 4 5-5" />
                </svg>
              }
            />
            <QuickLinkCard
              title="Parduotuvių paspaudimai"
              description="Išėjimo į parduotuves paspaudimų istorija ir statistika"
              to="/admin/shop_clicks"
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14L21 3m0 0h-6m6 0v6M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-5" />
                </svg>
              }
            />
            <QuickLinkCard
              title="Parduotuvių skelbimai"
              description="Scrapinti parduotuvių skelbimai ir kainos"
              to="/admin/shop-listings"
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
            />
            <QuickLinkCard
              title="UI komponentai"
              description="Komponentų biblioteka"
              to="/admin/components"
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              }
            />
            <QuickLinkCard
              title="OG Preview"
              description="Social media link preview"
              to="/admin/og-preview"
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              }
            />
          </div>

          {/* Database card */}
          <h2 className="mt-10 text-lg font-semibold text-gray-900">
            Duomenų bazė
          </h2>
          <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            {dbInfo ? (
              <div className="flex items-center justify-between">
                <div className="space-y-1 text-sm text-gray-600">
                  <p>
                    Dydis: <span className="font-medium text-gray-900">{dbInfo.size_human}</span>
                  </p>
                  {dbInfo.modified_at && (
                    <p>
                      Paskutinis pakeitimas:{" "}
                      <span className="font-medium text-gray-900">
                        {new Date(dbInfo.modified_at).toLocaleString("lt-LT")}
                      </span>
                    </p>
                  )}
                  <p>
                    Aplinka: <span className="font-medium text-gray-900">{dbInfo.environment}</span>
                  </p>
                </div>
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-900 disabled:opacity-50"
                >
                  {downloading ? "Siunčiama..." : "Atsisiųsti"}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Kraunama...</p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
